import type { Article, Issue, Style } from '@/constants';
import type { Db } from '@shared/storage';
import { getIssueIndexStore } from '@shared/sw/issue-index-store';
import type { AssetOwnerRecord } from '@shared/sw/eviction-queue';
import { responseByteSize } from '@shared/sw/response-size';
import { del, get, set } from 'idb-keyval';
import { collectIssueImages } from '@entities/issue';

import { collectArticleStyleIds } from './article-style-references';
import { collectStyleIdsUsedByOtherIssues } from './downloaded-issue';

export interface IssueStorageCategory {
	count: number;
	bytes: number;
}

/**
 * What a downloaded issue is holding, split so a UI can show as much or as
 * little of it as it wants (see `useIssueStorage`'s own doc comment for the
 * product reasoning behind each split).
 *
 * `totalBytes` is the **exclusive** footprint only -- `images.bytes +
 * articles.bytes + issueRow.bytes + exclusiveStyles.bytes` -- what this
 * issue alone is responsible for. `sharedStyles` is deliberately excluded
 * from it: those bytes are held in the shared `styles` table on another
 * downloaded issue's behalf too, so removing *this* issue doesn't reclaim
 * them (see `deleteStylesUnusedByOtherIssues`). It's reported alongside as
 * its own count/bytes for insight, never folded into the total.
 *
 * `images`: every image the issue's *articles* reference (`collectIssueImages`
 * -- see `sumIssueImages`'s own doc comment for why this replaced an
 * ownership-index-flag-based count). `articles`: the total byte size of the
 * article rows themselves as stored in Dexie (the row data, not the images
 * they reference).
 */
export interface IssueStorageBreakdown {
	issueId: string;
	totalBytes: number;
	images: IssueStorageCategory;
	articles: IssueStorageCategory;
	issueRow: { bytes: number };
	exclusiveStyles: IssueStorageCategory;
	sharedStyles: IssueStorageCategory;
}

function jsonByteSize(value: unknown): number {
	return new Blob([JSON.stringify(value)]).size;
}

function sumBytes<T>(items: Array<T>, size: (item: T) => number): IssueStorageCategory {
	return {
		count: items.length,
		bytes: items.reduce((total, item) => total + size(item), 0),
	};
}

/**
 * Sums every image the issue's articles actually reference (`collectIssueImages`
 * -- the same walk `DownloadIssueButton`/`useDownloadAssets` use to decide
 * what to fetch in the first place), not `cf-issue-index`'s `permanent` flag.
 *
 * `permanent` turned out not to be a reliable signal for "this issue's
 * images" in practice: a live check of a real downloaded issue found every
 * one of its cached image records sitting at `permanent: false` (recorded by
 * the incidental background-prefetch path, `scheduleDownload`'s default,
 * rather than `pinIssueAssets`), which made every image invisible to a
 * `permanent`-filtered sum -- a genuine issue with real cached images
 * reporting "0 MB". Sourcing the URL list from the articles themselves is
 * the same list of images the reader actually needs, regardless of which
 * code path happened to cache each one or whether it got pinned.
 *
 * `count` is every image the articles reference, whether or not it actually
 * resolved to a cached byte size (a URL that never finished downloading,
 * offline, still counts as one of the issue's images -- it just contributes
 * 0 bytes). `bytes` only sums what's verifiably still cached.
 *
 * Each resolvable URL is verified against real Cache Storage before its
 * stored `size` is trusted: nothing in this app reconciles `cf-issue-index`
 * against browser-driven eviction (a global storage-pressure cleanup outside
 * this app's control -- see the eviction system's own docs), so an entry can
 * in principle survive in the index after the browser silently dropped the
 * actual cached response. A stale entry found here is deleted on the spot
 * (self-healing).
 *
 * `size` is also backfilled here, not just verified: an entry written before
 * this field existed has no `size` at all, which would otherwise read as `0`
 * forever. The verification step already has the matched `Response` in
 * hand, so measuring and writing the size back costs nothing extra.
 *
 * Skipped entirely where the Cache Storage API isn't available (test
 * environments) -- stored sizes (if any) are trusted as-is in that case,
 * same effective number, just without the self-heal/backfill.
 */
async function sumIssueImages(
	articles: Array<Article>,
): Promise<IssueStorageCategory> {
	const urls = collectIssueImages(articles);
	if (!urls.length) {
		return { count: 0, bytes: 0 };
	}

	const store = getIssueIndexStore();
	const cacheAvailable = typeof caches !== 'undefined';

	const sizes = await Promise.all(
		urls.map(async (url): Promise<number> => {
			const record = await get<AssetOwnerRecord>(url, store);
			if (!record) {
				return 0;
			}
			if (!cacheAvailable) {
				return record.size ?? 0;
			}
			try {
				const cache = await caches.open(record.cacheName);
				const stillCached = await cache.match(url);
				if (!stillCached) {
					await del(url, store).catch(() => undefined);
					return 0;
				}
				if (record.size !== undefined) {
					return record.size;
				}
				const size = await responseByteSize(stillCached);
				await set(url, { ...record, size }, store).catch(() => undefined);
				return size ?? 0;
			} catch {
				// Best-effort: a Cache Storage failure here shouldn't make the
				// whole estimate throw, just skip this one URL's size.
				return record.size ?? 0;
			}
		}),
	);

	return {
		count: urls.length,
		bytes: sizes.reduce((total, size) => total + size, 0),
	};
}

/**
 * Per-issue storage breakdown for the downloads panel -- exclusive size only
 * (what removing this issue would actually free), plus counts/bytes for
 * every category so the caller can present as much detail as it wants. See
 * `useIssueStorage` for the React-facing hook that wraps this.
 */
export async function getIssueStorageBreakdown(
	db: Db,
	issueId: string,
): Promise<IssueStorageBreakdown> {
	const issue: Issue | null = await db.getIssue(issueId);
	if (!issue) {
		throw new Error(`No downloaded issue found for id "${issueId}"`);
	}

	const articles: Array<Article> = await db.getArticlesByIssue(issue.id);

	const usedStyleIds = new Set([
		...(issue.styleIds ?? []),
		...(await collectArticleStyleIds(db, articles)),
	]);
	const styles: Array<Style> = await db.getStylesByIds([...usedStyleIds]);
	const usedByOtherIssues = await collectStyleIdsUsedByOtherIssues(db, issue.id);

	const exclusiveStyles = styles.filter((s) => !usedByOtherIssues.has(`${s.id}`));
	const sharedStyles = styles.filter((s) => usedByOtherIssues.has(`${s.id}`));

	const images = await sumIssueImages(articles);
	const articlesCategory = sumBytes(articles, jsonByteSize);
	const issueRowBytes = jsonByteSize(issue);
	const exclusiveStylesCategory = sumBytes(exclusiveStyles, jsonByteSize);
	const sharedStylesCategory = sumBytes(sharedStyles, jsonByteSize);

	return {
		issueId: issue.id,
		totalBytes:
			images.bytes +
			articlesCategory.bytes +
			issueRowBytes +
			exclusiveStylesCategory.bytes,
		images,
		articles: articlesCategory,
		issueRow: { bytes: issueRowBytes },
		exclusiveStyles: exclusiveStylesCategory,
		sharedStyles: sharedStylesCategory,
	};
}
